import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FlaskConical, Lock, XCircle } from 'lucide-react';
import {
  platformSettingProblem,
  type PlatformSettingGroup,
  type PlatformSettingSpec,
} from '@esa/shared';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Field, Input, Select, Spinner, Switch, Textarea, useToast } from '../../ui/index.js';

/**
 * Platform settings (060), rendered from the registry in
 * `@esa/shared/platformSettings.ts` — one form for every group, so a new
 * setting is an entry there, not a new panel here.
 *
 * Each value says where it comes from: set here, from the deployment, or not
 * set. "Use deployment value" removes an override. Secrets are write-only.
 */

type Source = 'platform' | 'environment' | 'unset';
interface SettingView {
  spec: PlatformSettingSpec;
  value: string | null;
  set: boolean;
  source: Source;
  updatedAt: string | null;
}
interface Payload {
  groups: { key: PlatformSettingGroup; title: string; blurb: string }[];
  settings: SettingView[];
}

const SOURCE: Record<Source, string> = {
  platform: 'Set here',
  environment: 'From the deployment',
  unset: 'Not set',
};

/**
 * One small call with the saved settings. `via="gateway"` tests the gateway
 * whether or not it is switched on — the point is to know it works first.
 */
function ModelTest({ via }: { via?: 'gateway' }) {
  const [r, setR] = useState<{ ok: boolean; detail: string } | null>(null);
  const t = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; detail: string }>('/api/v1/platform/config/test-model', via ? { via } : {}),
    onSuccess: setR,
    onError: (e) => setR({ ok: false, detail: e instanceof ApiError ? e.message : 'The test could not run.' }),
  });
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <Button size="sm" variant="outline" leading={<FlaskConical />} onClick={() => t.mutate()} loading={t.isPending}>
        {via === 'gateway' ? 'Test the gateway' : 'Test the model'}
      </Button>
      {r && (
        <span className={`flex items-start gap-1 text-2xs ${r.ok ? 'text-ok' : 'text-danger'}`}>
          {r.ok ? <CheckCircle2 aria-hidden className="h-3.5 w-3.5 shrink-0" /> : <XCircle aria-hidden className="h-3.5 w-3.5 shrink-0" />}
          <span>{r.detail}</span>
        </span>
      )}
    </div>
  );
}

export function PlatformSettingsForm({ groups }: { groups: PlatformSettingGroup[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['platform-config'], queryFn: () => api.get<Payload>('/api/v1/platform/config') });
  const [draft, setDraft] = useState<Record<string, string | null>>({});

  const save = useMutation({
    mutationFn: (patch: Record<string, string | null>) => api.put('/api/v1/platform/config', patch),
    onSuccess: (_r, patch) => {
      setDraft((d) => {
        const next = { ...d };
        for (const k of Object.keys(patch)) delete next[k];
        return next;
      });
      for (const key of ['platform-config', 'platform-settings', 'platform-mail-reading']) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
      toast.success('Saved — in use within a minute');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <p className="text-xs text-ink-tertiary">Platform settings could not be loaded.</p>;

  const view = new Map(q.data.settings.map((s) => [s.spec.key, s]));

  return (
    <div className="space-y-4">
      {q.data.groups
        .filter((g) => groups.includes(g.key))
        .map((g) => {
          const settings = q.data!.settings.filter((s) => s.spec.group === g.key);
          const editable = settings.filter((s) => s.spec.editable !== false);
          const readOnly = settings.filter((s) => s.spec.editable === false);
          const pending = Object.keys(draft).filter((k) => view.get(k)?.spec.group === g.key);
          const problems = pending
            .map((k) => {
              const v = draft[k] ?? null;
              const spec = view.get(k)!.spec;
              if (v === null || v.trim() === '') return null;
              const why = platformSettingProblem(spec, v);
              return why ? `${spec.label} ${why}` : null;
            })
            .filter(Boolean) as string[];

          return (
            <section key={g.key} className="rounded-lg border border-line bg-surface p-4">
              <header className="mb-3">
                <h3 className="text-sm font-semibold text-ink">{g.title}</h3>
                <p className="max-w-prose text-2xs leading-relaxed text-ink-tertiary">{g.blurb}</p>
              </header>

              <div className="grid gap-3 sm:grid-cols-2">
                {editable.map((s) => {
                  const key = s.spec.key;
                  const inDraft = key in draft;
                  const current = inDraft ? draft[key] : s.value;
                  const hint = `${SOURCE[s.source]}${s.spec.env ? ` · ${s.spec.env}` : ''}. ${s.spec.help}`;
                  const set = (v: string | null) => setDraft({ ...draft, [key]: v });
                  const reset =
                    s.source === 'platform' && !inDraft ? (
                      <button
                        type="button"
                        className="text-3xs text-ink-tertiary underline hover:text-ink"
                        onClick={() => set(null)}
                      >
                        Use deployment value
                      </button>
                    ) : inDraft && draft[key] === null ? (
                      <span className="text-3xs text-warn">Will use the deployment value</span>
                    ) : null;

                  let control;
                  if (s.spec.type === 'bool') {
                    control = (
                      <Switch checked={(current ?? '') === 'true'} onCheckedChange={(on) => set(on ? 'true' : 'false')} label={s.spec.label} />
                    );
                  } else if (s.spec.type === 'enum') {
                    control = (
                      <Select
                        value={current ?? ''}
                        onValueChange={(v) => set(v)}
                        options={(s.spec.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
                        {...(s.spec.placeholder ? { placeholder: s.spec.placeholder } : {})}
                        aria-label={s.spec.label}
                      />
                    );
                  } else if (s.spec.type === 'secret') {
                    control = (
                      <Input
                        type="password"
                        value={inDraft ? (draft[key] ?? '') : ''}
                        placeholder={s.set ? '•••••••• (set — leave blank to keep)' : 'not set'}
                        onChange={(e) => (e.target.value === '' ? setDraft(omit(draft, key)) : set(e.target.value))}
                        aria-label={s.spec.label}
                      />
                    );
                  } else if (s.spec.type === 'prompt') {
                    // A prompt is paragraphs, not a value: a box to read and
                    // edit it in, with the default shown when it is blank.
                    control = (
                      <Textarea
                        rows={7}
                        value={current ?? ''}
                        placeholder={s.spec.placeholder}
                        onChange={(e) => set(e.target.value)}
                        aria-label={s.spec.label}
                        className="text-xs leading-relaxed"
                      />
                    );
                  } else {
                    control = (
                      <Input
                        type={s.spec.type === 'int' || s.spec.type === 'number' ? 'number' : 'text'}
                        value={current ?? ''}
                        placeholder={s.spec.placeholder}
                        onChange={(e) => set(e.target.value)}
                        aria-label={s.spec.label}
                        className={s.spec.type === 'url' ? 'font-mono' : undefined}
                      />
                    );
                  }
                  return (
                    <Field
                      key={key}
                      label={s.spec.type === 'bool' ? '' : s.spec.label}
                      hint={hint}
                      className={s.spec.type === 'prompt' ? 'sm:col-span-2' : undefined}
                    >
                      <div className="space-y-1">
                        {control}
                        {reset}
                      </div>
                    </Field>
                  );
                })}
              </div>

              {readOnly.length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink">
                    <Lock aria-hidden className="h-3.5 w-3.5 text-ink-tertiary" /> Set by the deployment
                  </h4>
                  <p className="mb-2 text-2xs text-ink-tertiary">
                    Needed before this screen can load, so they can only be changed in the deployment.
                  </p>
                  <ul className="grid gap-1.5 sm:grid-cols-2">
                    {readOnly.map((s) => (
                      <li key={s.spec.key} className="rounded-md border border-line-subtle p-2 text-2xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-ink">{s.spec.label}</span>
                          <span className="font-mono text-3xs text-ink-tertiary">{s.spec.env}</span>
                        </div>
                        <div className="mt-0.5 truncate font-mono text-ink-secondary">
                          {s.spec.type === 'secret' ? (s.set ? 'set' : 'not set') : s.value || 'not set'}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {editable.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  {g.key === 'model' ? <ModelTest /> : g.key === 'gateway' ? <ModelTest via="gateway" /> : <span />}
                  <div className="flex items-center gap-2">
                    {problems.length > 0 && <span className="text-2xs text-danger">{problems[0]}</span>}
                    <Button
                      variant="primary"
                      disabled={pending.length === 0 || problems.length > 0}
                      loading={save.isPending}
                      onClick={() => save.mutate(Object.fromEntries(pending.map((k) => [k, draft[k] ?? null])))}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )}
              {(g.key === 'model' || g.key === 'gateway') && (
                <p className="mt-2 text-3xs text-ink-tertiary">
                  The test uses the saved settings — save first.
                  {g.key === 'gateway' ? ' It goes through the gateway even while the gateway is switched off.' : ''}
                </p>
              )}
              {settings.some((s) => s.source === 'platform') && (
                <Badge tone="info" className="mt-3">
                  some values here override the deployment
                </Badge>
              )}
            </section>
          );
        })}
    </div>
  );
}

function omit<T extends Record<string, unknown>>(o: T, k: string): T {
  const { [k]: _drop, ...rest } = o;
  void _drop;
  return rest as T;
}
